import { FaCheckCircle, FaSpinner, FaLock, FaCreditCard } from 'react-icons/fa';
import { useTheme } from '../../context/SocketContext';

const StepCard = ({ step, title, status, progress, totalQuestions, answers, onClick, isLocked, onPayClick, completedAt, instruction, progressMessage }) => {
  useTheme();

  const getAnsweredCount = () => {
    if (step !== 1) return Number(progress) || 0;

    const stored = Number(progress) || 0;
    const total = Number(totalQuestions) || 0;

    if (total > 0 && stored >= 0 && stored <= total) {
      return stored;
    }

    if (total > 0 && stored > total) {
      return Math.min(total, Math.round((stored / 100) * total));
    }

    if (answers && typeof answers === 'object') {
      const count = Object.keys(answers).filter(
        (k) => answers[k] !== undefined && answers[k] !== null && answers[k] !== ''
      ).length;
      return total > 0 ? Math.min(count, total) : count;
    }

    return stored > 100 ? 100 : stored;
  };

  const answeredCount = getAnsweredCount();

  // Format completion date (date only, no time)
  const formatCompletionDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getProgressMessage = () => {
    if (progressMessage) return progressMessage;
    switch (step) {
      case 2:
        return "Working for Employee";
      case 3:
        return "Fill Form";
      case 4:
        return status === 'client-pending' ? 'Signature PDF' : 'Employee Draft Check';
      default:
        return "";
    }
  };

  const getStepInstructions = () => {
    if (instruction) return instruction;
    switch (step) {
      case 1:
        return "Upload Documents and Review Filled Checklist thereafter";
      case 2:
        return "Tax Return preparation after confirmation of Checklist (step-1)";
      case 3:
        return "Review draft tax return and submit Form 8879";
      case 4:
        return status === 'client-pending' ? 'Review and sign your document' : "We'll file your return after confirmation and share the acknowledgement";
      default:
        return "";
    }
  };

  const progressPercent = totalQuestions > 0
    ? Math.min(100, (answeredCount / totalQuestions) * 100)
    : (answeredCount >= 100 ? 100 : answeredCount);
  const progressPercentLabel = `${Math.round(progressPercent)}%`;

  const handleClick = () => {
    if (onClick) { onClick(step); }
  };

  // Get status badge text
  const getStatusBadge = () => {
    if (isLocked) return 'LOCKED';
    switch (status) {
      case 'in-progress':
        return 'IN PROGRESS';
      case 'client-pending':
        return 'CLIENT PENDING';
      case 'submitted':
      case 'completed':
        return 'COMPLETED';
      default:
        return 'PENDING';
    }
  };

  // Get card styles based on status
  const getCardStyles = () => {
    if (isLocked) return 'bg-gray-600 border-gray-500 opacity-75';
    switch (status) {
      case 'in-progress':
        return 'bg-[#1e2a3e] border-[#2d3f5a]';
      case 'client-pending':
        return 'bg-orange-600 border-orange-500';
      case 'submitted':
      case 'completed':
        return 'bg-green-600 border-green-500';
      default:
        return 'bg-blue-600 border-blue-500';
    }
  };

  // Get badge styles based on status
  const getBadgeStyles = () => {
    if (isLocked) return 'bg-gray-800 text-white';
    switch (status) {
      case 'in-progress':
        return 'bg-blue-600 text-white';
      case 'client-pending':
        return 'bg-orange-800 text-white';
      case 'submitted':
      case 'completed':
        return 'bg-green-800 text-white';
      default:
        return 'bg-blue-800 text-white';
    }
  };

  return (
    <div
      id={`step-card-${step}`}
      onClick={handleClick}
      className={`group relative w-full p-4 sm:p-5 rounded-2xl border cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col h-full ${getCardStyles()}`}
    >
      {/* Header with Step and Badge */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-white font-bold text-base sm:text-lg">Step {step}</span>
        <span className={`px-2.5 sm:px-3 py-1 rounded text-xs sm:text-xs font-bold uppercase tracking-wide ${getBadgeStyles()}`}>
          {getStatusBadge()}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-white font-semibold text-sm sm:text-base leading-snug flex-grow">
        {title}
      </h3>

      {/* Progress Bar - Only for Step 1 in progress */}
      {status === "in-progress" && step === 1 && (
        <div className="mt-auto pt-3">
          <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
            <span className="tracking-wide">Progress</span>
            <span className="font-semibold">{answeredCount}/{totalQuestions} • {progressPercentLabel}</span>
          </div>
          <div className="h-1.5 bg-gray-600 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500 bg-gray-400"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Status Message for in-progress steps (not step 1) */}
      {status === "in-progress" && step !== 1 && (
        <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-blue-300">
          <FaSpinner className="w-3 h-3 animate-spin" />
          <span>{getProgressMessage()}</span>
        </div>
      )}

      {/* Client Pending Status - Action Required */}
      {status === "client-pending" && (
        <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-orange-100">
          <FaSpinner className="w-3 h-3 animate-pulse" />
          <span>Action Required - {step === 3 ? 'Fill Form' : 'Signature PDF'}</span>
        </div>
      )}

      {/* Pending Status - Show progress message instead of "Waiting for previous step" */}
      {status === "pending" && (
        <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-blue-100">
          <span>{getProgressMessage()}</span>
        </div>
      )}

      {/* Completed Status */}
      {(status === "submitted" || status === "completed") && !isLocked && (
        <div className="mt-auto pt-2 space-y-1">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-green-100">
            <FaCheckCircle className="w-3 h-3" />
            <span>Completed</span>
          </div>
          {completedAt && (
            <div className="text-xs text-green-200 pl-5">
              {formatCompletionDate(completedAt)}
            </div>
          )}
        </div>
      )}

      {/* Locked Status with Pay Button */}
      {isLocked && (
        <div className="mt-auto pt-2 space-y-2">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-300">
            <FaLock className="w-3 h-3" />
            <span>Pay to Unlock</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onPayClick) onPayClick();
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 shadow-lg"
          >
            <FaCreditCard className="w-3 h-3" />
            <span>Pay & Unlock</span>
          </button>
        </div>
      )}

      {/* Tooltip on Hover */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 bg-dark-800 dark:bg-dark-700 text-white text-xs sm:text-sm rounded-2xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-normal text-center sm:text-left w-72 sm:w-80 max-w-[90vw] z-10">
        <span className="block">{getStepInstructions()}</span>
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-dark-800 dark:border-t-dark-700" />
      </div>
    </div>
  );
};

export default StepCard;